import { FormField } from "Abstracts/sde-e-form-field/sde-e-form-field";
import { render } from "lit-html";
import { uiElement, uiEvent } from "@kluntje/core";
import { CHANGED_EVENT } from "Constants/eventTypes";

import { TextInputTemplateArgs } from "./sde-e-text-input.template";

export class TextInput extends FormField<string> {
  @uiElement(".sde-e-form-field__input")
  private input: HTMLInputElement;

  private get minlength(): number | undefined {
    const curMinlength = this.getAttribute("minlength");
    if (curMinlength === null) return undefined;
    return parseInt(curMinlength, 10);
  }

  private get maxlength(): number | undefined {
    const curMaxlength = this.getAttribute("maxlength");
    if (curMaxlength === null) return undefined;
    return parseInt(curMaxlength, 10);
  }

  private get inputType(): string {
    return this.getAttribute("type") ?? "text";
  }

  private get valuePattern(): string | undefined {
    return this.getAttribute("pattern") || undefined;
  }

  private get initialValue(): string {
    return this.getAttribute("initial-value") || "";
  }

  private get curTabIndex(): string | undefined {
    return this.getAttribute("tabindex") || undefined;
  }

  public get currentValue(): string {
    return this.input.value || "";
  }

  public get submitString(): string | undefined {
    return this.submitValue;
  }

  private get currentTemplateArgs(): TextInputTemplateArgs {
    return {
      id: this.uuid,
      name: this.name,
      type: this.inputType,
      placeholder: this.placeholder,
      label: this.label,
      autocompleteType: this.autocompleteType,
      pattern: this.valuePattern,
      tabindex: this.curTabIndex,
      minlength: this.minlength,
      maxlength: this.maxlength,
      description: this.description,
      errorMessage: this.errorMessage,
      isRequired: this.isRequired,
      isDisabled: this.isDisabled,
      isReadonly: this.isReadonly,
      showError: this.state.showError,
    };
  }

  protected async initFormField() {
    if (this.initialValue !== "") {
      this.input.value = this.initialValue;
      await this.renderAsync();
    }
    await super.initFormField();
  }

  protected async prefillInput() {
    const prefillValue = this.prefillValue;
    if (prefillValue === null) return;
    this.input.value = prefillValue;
    await this.renderAsync();
  }

  public async silentReRender() {
    const { textInputTemplate } = await import("./sde-e-text-input.template");
    render(textInputTemplate(this.currentTemplateArgs), this.getUiRoot());
  }

  protected async renderAsync() {
    const { textInputTemplate } = await import("./sde-e-text-input.template");
    if (this.state.inValidationMode) await this.validate();
    render(textInputTemplate(this.currentTemplateArgs), this.getUiRoot());
  }

  @uiEvent("input", "input") // input event to catch changes from autofill in FF
  protected async handleInput() {
    await this.reRenderHook();
    this.dispatchEvent(new CustomEvent(CHANGED_EVENT, { bubbles: false }));
  }

  public updateValue(newValue: string) {
    this.input.value = newValue;
    this.reRenderHook();
  }

  public async validate(): Promise<void> {
    const isValid = await this.isValid();

    this.setState({ showError: !isValid, inValidationMode: true });
  }

  protected async handleFocusOut() {
    await super.handleFocusOut();
    const currentValue = this.submitValue || "";

    const isValid = await this.isValid();
    if (currentValue === "" && isValid) return;

    await this.validate();
    await this.reRenderHook();
  }

  public clearInput() {
    this.input.value = "";
    this.reRenderHook();
  }

  public async reset() {
    this.setState({ showError: false, inValidationMode: false });
    this.input.value = this.initialValue;
    await this.prefillInput();
    await this.reRenderHook();
  }
}

customElements.define("sde-e-text-input", TextInput);
